/**
 * ScorecardProfile — weighted scoring matrices (RICE, MoSCoW, ICE,
 * Kano, value-effort, opportunity).
 *
 * Scaffold profile for the Scorecard domain. Epic SP-B (#1387) fills
 * in the scoring engine (computed totals, sensitivity analysis,
 * auto-ranking), the 5 rendering modes (table, bubble, quadrant,
 * bar, radar), and the six bundled scoring methods.
 *
 * See plans/strategy-planning/domain.md § 2 for the canonical
 * scorecard grammar and scoring-method registry.
 */

import type { DomainDataProfile } from '../../../core/data-profiles';
import type { DiagramType } from '../../../lib/diagram/registries';

export type ScorecardNodeType = 'sc_option' | 'sc_criterion' | 'sc_method';
export type ScorecardEdgeType = 'scored_against' | 'outranks';

export const ScorecardProfile: DomainDataProfile<ScorecardNodeType, ScorecardEdgeType> = {
  domainId: 'scorecard',
  profileVersion: '1.0',
  diagramTypes: ['planning' as DiagramType],

  nodeTypes: ['sc_option', 'sc_criterion', 'sc_method'],
  edgeTypes: ['scored_against', 'outranks'],

  vocabulary: {
    recordSingular: 'Option',
    recordPlural: 'Options',
    repositoryLabel: 'Scorecard',
    hierarchyLabel: 'Rankings',
    importAction: 'Import Options',
    keyColumnHint: 'Option Name',
    commitLabel: 'Update Scorecard',
    pullLabel: 'Refresh Scorecard',
    diagramLabel: 'Scorecard',
  },

  fieldAliases: {
    name: ['option', 'title'],
    weight: ['importance', 'priority'],
    score: ['rating', 'value'],
    scale: ['range'],
    inverse: ['invert', 'lower-is-better'],
  },

  requiredProps: ['name'],
  recommendedProps: ['weight'],

  primaryShapeType: 'sc_option',

  propsToAttrsMapping: {
    name: 'name',
    method: 'method',
    weight: 'weight',
    score: 'score',
    scale: 'scale',
    inverse: 'inverse',
    total: 'total',
    rank: 'rank',
  },

  relationshipPackId: 'planning-relationships',
};
